import { useState, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import {
  Upload,
  FileSpreadsheet,
  ArrowRight,
  ArrowLeft,
  Check,
  AlertCircle,
  Loader2,
  Users,
  Heart,
  Calendar,
  Star,
  Download,
  Cloud,
} from 'lucide-react';

type ImportMode = 'general' | 'shulcloud';
type ImportType = 'members' | 'donations' | 'yahrzeits' | 'events';

interface FieldDefinition {
  field: string;
  label: string;
  required: boolean;
}

interface TypeDefinition {
  type: ImportType;
  label: string;
  fields: FieldDefinition[];
}

interface ImportSession {
  sessionId: string;
  headers: string[];
  mapping: Record<string, string>;
  totalRows: number;
  fields: FieldDefinition[];
}

interface PreviewRow {
  rowNumber: number;
  raw: Record<string, string>;
  mapped: Record<string, string>;
}

type Step = 'select-type' | 'upload' | 'map-fields' | 'preview' | 'import' | 'complete';

const typeIcons: Record<ImportType, typeof Users> = {
  members: Users,
  donations: Heart,
  yahrzeits: Star,
  events: Calendar,
};

const typeDescriptions: Record<ImportType, string> = {
  members: 'Import member profiles including contact info, addresses, and membership details',
  donations: 'Import donation records with amounts, dates, and donor information',
  yahrzeits: 'Import yahrzeit memorial dates linked to existing members',
  events: 'Import events with dates, times, and locations',
};

export function ImportPage() {
  const [mode, setMode] = useState<ImportMode>('general');
  const [step, setStep] = useState<Step>('select-type');
  const [selectedType, setSelectedType] = useState<ImportType | null>(null);
  const [session, setSession] = useState<ImportSession | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [importOptions, setImportOptions] = useState({
    skipDuplicates: true,
    updateExisting: false,
  });

  // ShulCloud-specific state
  const [scSession, setScSession] = useState<{
    sessionId: string;
    totalRows: number;
    columns: string[];
  } | null>(null);
  const [scStep, setScStep] = useState<'select-type' | 'upload' | 'preview' | 'migrate' | 'complete'>('select-type');
  const [scType, setScType] = useState<'members' | 'donations' | 'yahrzeits' | null>(null);
  const [scPreviewData, setScPreviewData] = useState<{
    preview: { rowNumber: number; data: Record<string, string>; mapped: Record<string, string> }[];
    totalRows: number;
    type: string;
  } | null>(null);
  const [scResult, setScResult] = useState<{
    totalRows: number;
    processedRows: number;
    skippedRows: number;
    errors: { row: number; error: string }[];
  } | null>(null);

  // Fetch ShulCloud migration info
  const { data: scInfoData } = useQuery({
    queryKey: ['shulcloud-info'],
    queryFn: async () => {
      const response = await api.get<{
        supportedTypes: {
          type: string;
          label: string;
          description: string;
          requiredColumns: string[];
          optionalColumns: string[];
        }[];
      }>('/shulcloud/info');
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    enabled: mode === 'shulcloud',
  });

  // ShulCloud upload mutation
  const scUploadMutation = useMutation({
    mutationFn: async (params: { type: 'members' | 'donations' | 'yahrzeits'; filename: string; content: string }) => {
      const response = await api.post<{ sessionId: string; totalRows: number; columns: string[] }>(
        '/shulcloud/upload',
        params
      );
      if (!response.success || !response.data) throw new Error(response.error?.message || 'Upload failed');
      return response.data;
    },
    onSuccess: (data) => {
      if (data) {
        setScSession(data);
        setScStep('preview');
      }
    },
  });

  // ShulCloud preview query
  const {
    data: scPreviewQueryData,
    isLoading: scPreviewLoading,
    refetch: refetchScPreview,
  } = useQuery({
    queryKey: ['shulcloud-preview', scSession?.sessionId],
    queryFn: async () => {
      const response = await api.get<{
        preview: { rowNumber: number; data: Record<string, string>; mapped: Record<string, string> }[];
        totalRows: number;
        type: string;
      }>(`/shulcloud/sessions/${scSession!.sessionId}/preview?limit=10`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    enabled: scStep === 'preview' && !!scSession,
  });

  // ShulCloud execute mutation
  const scExecuteMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post<{
        totalRows: number;
        processedRows: number;
        skippedRows: number;
        errors: { row: number; error: string }[];
      }>(`/shulcloud/sessions/${scSession!.sessionId}/execute`, { options: { skipDuplicates: true } });
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: (data) => {
      if (data) {
        setScResult(data);
        setScStep('complete');
      }
    },
  });

  // Fetch available import types
  const { data: typesData } = useQuery({
    queryKey: ['import-types'],
    queryFn: async () => {
      const response = await api.get<{ types: TypeDefinition[] }>('/imports/types');
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (params: { type: ImportType; filename: string; content: string }) => {
      const response = await api.post<ImportSession>('/imports/upload', params);
      if (!response.success || !response.data) throw new Error(response.error?.message || 'Upload failed');
      return response.data;
    },
    onSuccess: (data) => {
      if (data) {
        setSession(data);
        setMapping(data.mapping);
        setStep('map-fields');
      }
    },
  });

  // Update mapping mutation
  const updateMappingMutation = useMutation({
    mutationFn: async (params: { sessionId: string; mapping: Record<string, string> }) => {
      const response = await api.patch(`/imports/sessions/${params.sessionId}/mapping`, {
        mapping: params.mapping,
      });
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  // Preview query
  const {
    data: previewData,
    isLoading: previewLoading,
    refetch: refetchPreview,
  } = useQuery({
    queryKey: ['import-preview', session?.sessionId],
    queryFn: async () => {
      const response = await api.get<{
        preview: PreviewRow[];
        totalRows: number;
        missingRequired: string[];
        warnings: string[];
      }>(`/imports/sessions/${session!.sessionId}/preview?limit=10`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    enabled: step === 'preview' && !!session,
  });

  // Execute import mutation
  const executeMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post<{
        totalRows: number;
        processedRows: number;
        errors: { row: number; error: string }[];
      }>(`/imports/sessions/${session!.sessionId}/execute`, { options: importOptions });
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      setStep('complete');
    },
  });

  // Handle file upload
  const handleFileUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || !selectedType) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        uploadMutation.mutate({
          type: selectedType,
          filename: file.name,
          content,
        });
      };
      reader.readAsText(file);
    },
    [selectedType, uploadMutation]
  );

  // Handle drag and drop
  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const file = event.dataTransfer.files?.[0];
      if (!file || !selectedType) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        uploadMutation.mutate({
          type: selectedType,
          filename: file.name,
          content,
        });
      };
      reader.readAsText(file);
    },
    [selectedType, uploadMutation]
  );

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  // Move to next step
  const handleNext = async () => {
    if (step === 'map-fields' && session) {
      await updateMappingMutation.mutateAsync({ sessionId: session.sessionId, mapping });
      setStep('preview');
    } else if (step === 'preview') {
      setStep('import');
    } else if (step === 'import') {
      executeMutation.mutate();
    }
  };

  // Move to previous step
  const handleBack = () => {
    if (step === 'upload') setStep('select-type');
    else if (step === 'map-fields') setStep('upload');
    else if (step === 'preview') setStep('map-fields');
    else if (step === 'import') setStep('preview');
  };

  // Reset wizard
  const handleReset = () => {
    setStep('select-type');
    setSelectedType(null);
    setSession(null);
    setMapping({});
  };

  // ShulCloud file upload handler
  const handleScFileUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || !scType) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        scUploadMutation.mutate({
          type: scType,
          filename: file.name,
          content,
        });
      };
      reader.readAsText(file);
    },
    [scType, scUploadMutation]
  );

  // ShulCloud drag and drop handler
  const handleScDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const file = event.dataTransfer.files?.[0];
      if (!file || !scType) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        scUploadMutation.mutate({
          type: scType,
          filename: file.name,
          content,
        });
      };
      reader.readAsText(file);
    },
    [scType, scUploadMutation]
  );

  // Reset ShulCloud wizard
  const handleScReset = () => {
    setScStep('select-type');
    setScType(null);
    setScSession(null);
    setScPreviewData(null);
    setScResult(null);
  };

  // Switch mode handler
  const handleModeSwitch = (newMode: ImportMode) => {
    setMode(newMode);
    // Reset both wizards
    handleReset();
    handleScReset();
  };

  const types = typesData?.types || [];
  const scTypes = scInfoData?.supportedTypes || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Import Data</h1>
          <p className="mt-1 text-sm text-gray-500">
            {mode === 'general'
              ? 'Import members, donations, yahrzeits, or events from CSV files'
              : 'Migrate data from ShulCloud exports'}
          </p>
        </div>

        {/* Mode Toggle */}
        <div className="flex rounded-lg border border-gray-200 bg-gray-100 p-1">
          <button
            onClick={() => handleModeSwitch('general')}
            className={cn(
              'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
              mode === 'general'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            )}
          >
            <Upload className="h-4 w-4" />
            General Import
          </button>
          <button
            onClick={() => handleModeSwitch('shulcloud')}
            className={cn(
              'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
              mode === 'shulcloud'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            )}
          >
            <Cloud className="h-4 w-4" />
            ShulCloud Migration
          </button>
        </div>
      </div>

      {/* General Import Mode */}
      {mode === 'general' && (
        <>
      {/* Progress Steps */}
      <div className="flex items-center justify-center">
        <div className="flex items-center space-x-2">
          {(['select-type', 'upload', 'map-fields', 'preview', 'import', 'complete'] as Step[]).map(
            (s, index) => (
              <div key={s} className="flex items-center">
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium',
                    step === s
                      ? 'bg-primary-600 text-white'
                      : ['select-type', 'upload', 'map-fields', 'preview', 'import', 'complete'].indexOf(
                          step
                        ) > index
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  )}
                >
                  {['select-type', 'upload', 'map-fields', 'preview', 'import', 'complete'].indexOf(
                    step
                  ) > index ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    index + 1
                  )}
                </div>
                {index < 5 && (
                  <div
                    className={cn(
                      'h-0.5 w-8',
                      ['select-type', 'upload', 'map-fields', 'preview', 'import', 'complete'].indexOf(
                        step
                      ) > index
                        ? 'bg-green-600'
                        : 'bg-gray-200'
                    )}
                  />
                )}
              </div>
            )
          )}
        </div>
      </div>

      {/* Step: Select Type */}
      {step === 'select-type' && (
        <div className="grid gap-4 sm:grid-cols-2">
          {types.map((type) => {
            const Icon = typeIcons[type.type as ImportType];
            return (
              <button
                key={type.type}
                onClick={() => {
                  setSelectedType(type.type as ImportType);
                  setStep('upload');
                }}
                className="flex items-start gap-4 rounded-xl border border-gray-200 bg-white p-6 text-left transition-all hover:border-primary-300 hover:shadow-md"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100">
                  <Icon className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{type.label}</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {typeDescriptions[type.type as ImportType]}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Step: Upload */}
      {step === 'upload' && (
        <div className="mx-auto max-w-xl">
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="relative rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center transition-colors hover:border-primary-400"
          >
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="absolute inset-0 cursor-pointer opacity-0"
            />
            <FileSpreadsheet className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">Upload CSV File</h3>
            <p className="mt-2 text-sm text-gray-500">
              Drag and drop your CSV file here, or click to browse
            </p>
            {uploadMutation.isPending && (
              <div className="mt-4">
                <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary-600" />
                <p className="mt-2 text-sm text-gray-500">Processing file...</p>
              </div>
            )}
            {uploadMutation.isError && (
              <div className="mt-4 flex items-center justify-center gap-2 text-red-600">
                <AlertCircle className="h-5 w-5" />
                <span className="text-sm">
                  {uploadMutation.error instanceof Error
                    ? uploadMutation.error.message
                    : 'Upload failed'}
                </span>
              </div>
            )}
          </div>

          <div className="mt-6 flex items-center justify-between">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <a
              href={`/templates/${selectedType}-template.csv`}
              download
              className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
            >
              <Download className="h-4 w-4" />
              Download template
            </a>
          </div>
        </div>
      )}

      {/* Step: Map Fields */}
      {step === 'map-fields' && session && (
        <div className="mx-auto max-w-3xl">
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Map CSV Columns to Fields</h2>
            <p className="text-sm text-gray-500 mb-6">
              Match your CSV columns to the corresponding fields. Required fields are marked with *.
            </p>

            <div className="space-y-4">
              {session.headers.map((header) => (
                <div key={header} className="flex items-center gap-4">
                  <div className="w-1/3">
                    <span className="text-sm font-medium text-gray-700">{header}</span>
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-400" />
                  <div className="flex-1">
                    <select
                      value={mapping[header] || ''}
                      onChange={(e) => setMapping({ ...mapping, [header]: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    >
                      <option value="">-- Skip this column --</option>
                      {session.fields.map((field) => (
                        <option key={field.field} value={field.field}>
                          {field.label}
                          {field.required ? ' *' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <button
              onClick={handleNext}
              disabled={updateMappingMutation.isPending}
              className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {updateMappingMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )}
              Preview Import
            </button>
          </div>
        </div>
      )}

      {/* Step: Preview */}
      {step === 'preview' && session && (
        <div className="space-y-6">
          {previewLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : previewData ? (
            <>
              {/* Warnings */}
              {previewData.warnings.length > 0 && (
                <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-4">
                  <div className="flex items-center gap-2 text-yellow-700">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-medium">Warnings</span>
                  </div>
                  <ul className="mt-2 text-sm text-yellow-600 list-disc list-inside">
                    {previewData.warnings.map((warning, i) => (
                      <li key={i}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Preview Table */}
              <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Preview</h2>
                  <p className="text-sm text-gray-500">
                    Showing {previewData.preview.length} of {previewData.totalRows} rows
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Row
                        </th>
                        {session.fields
                          .filter((f) => Object.values(mapping).includes(f.field))
                          .map((field) => (
                            <th
                              key={field.field}
                              className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                            >
                              {field.label}
                            </th>
                          ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {previewData.preview.map((row) => (
                        <tr key={row.rowNumber}>
                          <td className="px-4 py-3 text-sm text-gray-500">{row.rowNumber}</td>
                          {session.fields
                            .filter((f) => Object.values(mapping).includes(f.field))
                            .map((field) => (
                              <td key={field.field} className="px-4 py-3 text-sm text-gray-900">
                                {row.mapped[field.field] || '-'}
                              </td>
                            ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : null}

          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <button
              onClick={handleNext}
              disabled={previewData?.missingRequired && previewData.missingRequired.length > 0}
              className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              <ArrowRight className="h-4 w-4" />
              Continue to Import
            </button>
          </div>
        </div>
      )}

      {/* Step: Import Options */}
      {step === 'import' && session && (
        <div className="mx-auto max-w-xl">
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Import Options</h2>

            <div className="space-y-4">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={importOptions.skipDuplicates}
                  onChange={(e) =>
                    setImportOptions({ ...importOptions, skipDuplicates: e.target.checked })
                  }
                  className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <div>
                  <span className="font-medium text-gray-900">Skip duplicates</span>
                  <p className="text-sm text-gray-500">
                    Skip rows where a matching record already exists (based on email for members)
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={importOptions.updateExisting}
                  onChange={(e) =>
                    setImportOptions({ ...importOptions, updateExisting: e.target.checked })
                  }
                  className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <div>
                  <span className="font-medium text-gray-900">Update existing records</span>
                  <p className="text-sm text-gray-500">
                    Update existing records with data from the import file
                  </p>
                </div>
              </label>
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>Ready to import:</strong> {session.totalRows} rows
              </p>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <button
              onClick={handleNext}
              disabled={executeMutation.isPending}
              className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
            >
              {executeMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Start Import
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step: Complete */}
      {step === 'complete' && executeMutation.data && (
        <div className="mx-auto max-w-xl">
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="mt-4 text-xl font-semibold text-gray-900">Import Complete!</h2>
            <p className="mt-2 text-gray-500">
              Successfully imported {executeMutation.data.processedRows} of{' '}
              {executeMutation.data.totalRows} rows.
            </p>

            {executeMutation.data.errors.length > 0 && (
              <div className="mt-6 text-left">
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  Errors ({executeMutation.data.errors.length})
                </h3>
                <div className="max-h-40 overflow-y-auto rounded-lg bg-red-50 p-4">
                  {executeMutation.data.errors.map((error, i) => (
                    <p key={i} className="text-sm text-red-600">
                      Row {error.row}: {error.error}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={handleReset}
              className="mt-6 rounded-lg bg-primary-600 px-6 py-2 text-sm font-medium text-white hover:bg-primary-700"
            >
              Import More Data
            </button>
          </div>
        </div>
      )}
        </>
      )}

      {/* ShulCloud Migration Mode */}
      {mode === 'shulcloud' && (
        <>
          {/* ShulCloud Info Banner */}
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-start gap-3">
              <Cloud className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-900">ShulCloud Migration</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Upload exports from ShulCloud. The system will automatically map ShulCloud column names
                  to OpenShul fields. For best results, import members first, then donations and yahrzeits.
                </p>
              </div>
            </div>
          </div>

          {/* ShulCloud Progress Steps */}
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-2">
              {(['select-type', 'upload', 'preview', 'migrate', 'complete'] as typeof scStep[]).map(
                (s, index) => (
                  <div key={s} className="flex items-center">
                    <div
                      className={cn(
                        'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium',
                        scStep === s
                          ? 'bg-primary-600 text-white'
                          : ['select-type', 'upload', 'preview', 'migrate', 'complete'].indexOf(scStep) > index
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-200 text-gray-600'
                      )}
                    >
                      {['select-type', 'upload', 'preview', 'migrate', 'complete'].indexOf(scStep) > index ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        index + 1
                      )}
                    </div>
                    {index < 4 && (
                      <div
                        className={cn(
                          'h-0.5 w-8',
                          ['select-type', 'upload', 'preview', 'migrate', 'complete'].indexOf(scStep) > index
                            ? 'bg-green-600'
                            : 'bg-gray-200'
                        )}
                      />
                    )}
                  </div>
                )
              )}
            </div>
          </div>

          {/* ShulCloud Step: Select Type */}
          {scStep === 'select-type' && (
            <div className="grid gap-4 sm:grid-cols-3">
              {scTypes.map((type) => {
                const icons: Record<string, typeof Users> = {
                  members: Users,
                  donations: Heart,
                  yahrzeits: Star,
                };
                const Icon = icons[type.type] || Users;
                return (
                  <button
                    key={type.type}
                    onClick={() => {
                      setScType(type.type as 'members' | 'donations' | 'yahrzeits');
                      setScStep('upload');
                    }}
                    className="flex flex-col items-center gap-3 rounded-xl border border-gray-200 bg-white p-6 text-center transition-all hover:border-primary-300 hover:shadow-md"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100">
                      <Icon className="h-6 w-6 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{type.label}</h3>
                      <p className="mt-1 text-sm text-gray-500">{type.description}</p>
                    </div>
                    <div className="mt-2 text-xs text-gray-400">
                      Required: {type.requiredColumns.join(', ')}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* ShulCloud Step: Upload */}
          {scStep === 'upload' && scType && (
            <div className="mx-auto max-w-xl">
              <div
                onDrop={handleScDrop}
                onDragOver={handleDragOver}
                className="relative rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center transition-colors hover:border-primary-400"
              >
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleScFileUpload}
                  className="absolute inset-0 cursor-pointer opacity-0"
                />
                <Cloud className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">
                  Upload ShulCloud {scType.charAt(0).toUpperCase() + scType.slice(1)} Export
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Export from ShulCloud and upload the CSV file here
                </p>
                {scUploadMutation.isPending && (
                  <div className="mt-4">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary-600" />
                    <p className="mt-2 text-sm text-gray-500">Processing file...</p>
                  </div>
                )}
                {scUploadMutation.isError && (
                  <div className="mt-4 flex items-center justify-center gap-2 text-red-600">
                    <AlertCircle className="h-5 w-5" />
                    <span className="text-sm">
                      {scUploadMutation.error instanceof Error
                        ? scUploadMutation.error.message
                        : 'Upload failed'}
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-6">
                <button
                  onClick={() => setScStep('select-type')}
                  className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
              </div>
            </div>
          )}

          {/* ShulCloud Step: Preview */}
          {scStep === 'preview' && scSession && (
            <div className="space-y-6">
              {scPreviewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                </div>
              ) : scPreviewQueryData ? (
                <>
                  {/* Preview Info */}
                  <div className="rounded-lg bg-green-50 border border-green-200 p-4">
                    <div className="flex items-center gap-2 text-green-700">
                      <Check className="h-5 w-5" />
                      <span className="font-medium">
                        File validated! {scPreviewQueryData.totalRows} rows ready to migrate.
                      </span>
                    </div>
                  </div>

                  {/* Preview Table */}
                  <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h2 className="text-lg font-semibold text-gray-900">Preview Mapped Data</h2>
                      <p className="text-sm text-gray-500">
                        Showing {scPreviewQueryData.preview.length} of {scPreviewQueryData.totalRows} rows
                      </p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Row
                            </th>
                            {scPreviewQueryData.preview[0] &&
                              Object.keys(scPreviewQueryData.preview[0].mapped).slice(0, 6).map((key) => (
                                <th
                                  key={key}
                                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                                >
                                  {key}
                                </th>
                              ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {scPreviewQueryData.preview.map((row) => (
                            <tr key={row.rowNumber}>
                              <td className="px-4 py-3 text-sm text-gray-500">{row.rowNumber}</td>
                              {Object.entries(row.mapped).slice(0, 6).map(([key, value]) => (
                                <td key={key} className="px-4 py-3 text-sm text-gray-900">
                                  {value || '-'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              ) : null}

              <div className="flex items-center justify-between">
                <button
                  onClick={() => {
                    setScStep('upload');
                    setScSession(null);
                  }}
                  className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
                <button
                  onClick={() => setScStep('migrate')}
                  className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                >
                  <ArrowRight className="h-4 w-4" />
                  Continue to Migrate
                </button>
              </div>
            </div>
          )}

          {/* ShulCloud Step: Migrate */}
          {scStep === 'migrate' && scSession && (
            <div className="mx-auto max-w-xl">
              <div className="rounded-xl border border-gray-200 bg-white p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Ready to Migrate</h2>

                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>Records to migrate:</strong> {scSession.totalRows}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Duplicate records will be automatically skipped based on email address.
                    </p>
                  </div>

                  <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> For yahrzeits, make sure to import members first so they can be properly linked.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <button
                  onClick={() => setScStep('preview')}
                  className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
                <button
                  onClick={() => scExecuteMutation.mutate()}
                  disabled={scExecuteMutation.isPending}
                  className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {scExecuteMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Migrating...
                    </>
                  ) : (
                    <>
                      <Cloud className="h-4 w-4" />
                      Start Migration
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* ShulCloud Step: Complete */}
          {scStep === 'complete' && scResult && (
            <div className="mx-auto max-w-xl">
              <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="mt-4 text-xl font-semibold text-gray-900">Migration Complete!</h2>
                <p className="mt-2 text-gray-500">
                  Successfully migrated {scResult.processedRows} of {scResult.totalRows} rows.
                  {scResult.skippedRows > 0 && (
                    <span className="block text-sm text-gray-400 mt-1">
                      ({scResult.skippedRows} duplicates skipped)
                    </span>
                  )}
                </p>

                {scResult.errors.length > 0 && (
                  <div className="mt-6 text-left">
                    <h3 className="text-sm font-medium text-gray-900 mb-2">
                      Errors ({scResult.errors.length})
                    </h3>
                    <div className="max-h-40 overflow-y-auto rounded-lg bg-red-50 p-4">
                      {scResult.errors.map((error, i) => (
                        <p key={i} className="text-sm text-red-600">
                          Row {error.row}: {error.error}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={handleScReset}
                  className="mt-6 rounded-lg bg-primary-600 px-6 py-2 text-sm font-medium text-white hover:bg-primary-700"
                >
                  Migrate More Data
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
